import React, { useEffect, useState } from "react";
import AnimatedDialogLayout from "./AnimatedDialogLayout";
import useSearchFilter from "hooks/useSearchFilter";
import { ReactComponent as CalculatorIcon } from "assets/icons/calculator_fill.svg";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { ReactComponent as ArrowDown } from "assets/icons/slider_arrow_right.svg";
import SquireCheckbox from "components/Checkbox/SquireCheckobx";
import { formatCurrencyInEuro } from "utils/formatNumber";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ReactComponent as InfoIcon } from "assets/icons/info_2.svg";
import { toast } from "react-toastify";
import housePriceCalculatorAlogrithm from "utils/housePriceCalculatorAlogrithm";
import { useMediaQuery } from "react-responsive";
import { sendHouseReportPDF } from "api/property";
import ButtonSpinner from "components/Loading/ButtonSpinner";
import useAuth from "hooks/useAuth";
// import { sendPdfReport } from "utils/emailService";

const ExpandableSection = ({
  title,
  infoText,
  options,
  selectedOption,
  onOptionChange,
  inputFields = [],
  onExpandToggle = () => {},
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleOptionChange = (id) => {
    onOptionChange(id);
  };

  const selectedOptionLabel = options.find(
    (option) => option.id === selectedOption
  )?.label;

  const handleSectionClick = (e) => {
    if (e.target.closest("input")) return;
    setIsExpanded((prev) => !prev);
    onExpandToggle();
  };

  return (
    <div
      style={{ paddingBottom: isExpanded ? 12 : 0 }}
      className="bg-[#F2F2F2] cursor-pointer border-[0.5px] border-[#E5E5E5] rounded-[6px] pl-4 pr-2"
    >
      <div
        onClick={handleSectionClick}
        className="h-[48px] flex items-center justify-between"
      >
        <p
          className={`text-primary text-[16px] leading-[1.3] font-normal ${
            selectedOption ? "font-semibold" : ""
          }`}
        >
          {selectedOptionLabel || title}
        </p>

        <div className="flex items-center ">
          <span
            className="cursor-pointer mr-2"
            data-tooltip-id="pricing_calculator"
            data-tooltip-content={infoText}
          >
            <InfoIcon
              width={15}
              height={15}
              className="text-primary fill-primary"
            />
          </span>
          {/* Info Tooltip  */}
          <ReactTooltip
            id="pricing_calculator"
            place="top"
            positionStrategy="fixed"
            className="!max-w-[300px] !z-10 !text-sm !text-gray-500 !bg-white border !border-gray-200 !rounded-lg !shadow-xs"
          />
          {inputFields.length > 0 && selectedOption && !isExpanded && (
            <div className="flex items-center space-x-2">
              {inputFields.map((field, index) => (
                <div
                  key={index}
                  className="relative flex items-center w-[131px]"
                >
                  <input
                    type="text"
                    value={field.value ? formatCurrencyInEuro(field.value) : ""}
                    onChange={field.onChange}
                    className="bg-[#FFFFFF] h-[34px] outline-none w-full rounded-[5px] px-[13px] text-primary font-semibold text-[14px] pr-[24px] text-right"
                  />
                  <span className="absolute right-[10px] text-primary text-[16px] font-normal">
                    €
                  </span>
                </div>
              ))}
            </div>
          )}

          {(selectedOption === 7 || selectedOption === 9) && !isExpanded ? (
            ""
          ) : (
            <div className="w-[20px] h-[26px] cursor-pointer flex items-center justify-center">
              <ArrowDown
                className={`stroke-primary text-primary ${
                  isExpanded ? "rotate-90" : ""
                } w-[28px] h-[30px] transition-all duration-300 ease-in-out`}
              />
            </div>
          )}
        </div>
      </div>

      {isExpanded && (
        <>
          {options.map((option) => (
            <div
              key={option.id}
              className="hover:bg-[#F4F4F4] transition-all pl-[10px] duration-300 ease-in-out h-[35px]"
            >
              <SquireCheckbox
                id={option.id}
                checked={selectedOption === option.id}
                label={option.label}
                labelStyle={{
                  color: "#1DA1F2",
                  fontWeight: 600,
                  fontSize: 16,
                  lineHeight: 1.3,
                }}
                checkboxStyle={{
                  width: 20,
                  height: 20,
                  minWidth: 20,
                  minHeight: 20,
                }}
                isCalculator={true}
                onChange={() => handleOptionChange(option.id)}
              />
            </div>
          ))}

          {/* Conditionally render the input fields */}
          {inputFields.length > 0 &&
            inputFields.map((field, index) => (
              <div key={index} className="my-4">
                <p className="text-primary text-[16px] font-normal">
                  {field.label}
                </p>

                <div
                  key={index}
                  className="relative flex items-center w-[131px]"
                >
                  <input
                    type="text"
                    value={field.value ? formatCurrencyInEuro(field.value) : ""}
                    onChange={field.onChange}
                    className="bg-[#FFFFFF] h-[34px] outline-none w-full rounded-[5px] px-[13px] text-primary font-semibold text-[14px] pr-[24px] text-right"
                  />
                  <span className="absolute right-[10px] text-primary text-[16px] font-normal">
                    €
                  </span>
                </div>
              </div>
            ))}
        </>
      )}
    </div>
  );
};

const HousePriceCalculatorDialog = ({ id }) => {
  const { isHousePriceCalculatorDialogOpen, toggleHousePriceCalculatorDialog } =
    useSearchFilter();
  const [propertyValue, setPropertyValue] = useState("");
  const [selectedFirstHomeOption, setSelectedFirstHomeOption] = useState(null);
  const [selectedSellerOption, setSelectedSellerOption] = useState(null);
  const [selectedAgencyOption, setSelectedAgencyOption] = useState(null);
  const [selectedMortgageOption, setSelectedMortgageOption] = useState(null);
  const [selectedRenditaOption, setSelectedRenditaOption] = useState(null);

  const [mortgageAmount, setMortgageAmount] = useState("");
  const [isMortgageExpanded, setIsMortgageExpanded] = useState(false);
  const [renditaCatastale, setRenditaCatastale] = useState("");
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const MAX_EMAILS = 10; // Max 10 emails can be invite
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [generatedPdfBlob, setGeneratedPdfBlob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMediaQuery({ query: "(max-width: 530px)" });
  const { user } = useAuth();

  useEffect(() => {
    if (!isHousePriceCalculatorDialogOpen) {
      setPropertyValue("");
      setSelectedFirstHomeOption(null);
      setSelectedSellerOption(null);
      setSelectedAgencyOption(null);
      setSelectedMortgageOption(null);
      setSelectedRenditaOption(null);
      setMortgageAmount("");
      setRenditaCatastale("");
      setEmailInput("");
      setEmails([]);
      setIsSubmitted(false);
    }
  }, [isHousePriceCalculatorDialogOpen]);

  const firstHomeOptions = [
    { id: 1, label: "Acquisto come prima casa" },
    { id: 2, label: "Acquisto come seconda casa" },
  ];

  const sellerOptions = [
    { id: 3, label: "Vende un Privato" },
    { id: 4, label: "Vende un'impresa" },
  ];

  const agencyOptions = [
    { id: 5, label: "Acquisto tramite Agenzia" },
    { id: 6, label: "Acquisto senza intermediazione" },
  ];

  const mortgageOptions = [
    { id: 7, label: "Acquisto con mutuo" },
    { id: 8, label: "Non acquisto con mutuo" },
  ];

  const renditaOptions = [
    { id: 9, label: "Sì la conosco" },
    { id: 10, label: "No, il calcolo verrà eseguito senza imposte" },
  ];

  const handlePropertyChange = (e) => {
    const formattedValue = e.target.value.replace(/\D/g, "");
    setPropertyValue(formattedValue);
  };

  const isFormValid = () => {
    const isOptionSelected =
      selectedFirstHomeOption &&
      selectedSellerOption &&
      selectedAgencyOption &&
      selectedMortgageOption &&
      selectedRenditaOption;

    const isMortgageValid =
      selectedMortgageOption === 7 ? mortgageAmount !== "" : true;
    const isRenditaValid =
      selectedRenditaOption === 9 ? renditaCatastale !== "" : true;

    return (
      isOptionSelected &&
      isMortgageValid &&
      isRenditaValid &&
      propertyValue !== ""
    );
  };

  const toggleMortgageExpand = () => {
    setIsMortgageExpanded((prevState) => !prevState);
  };

  useEffect(() => {
    if (!isMortgageExpanded) {
      if (mortgageAmount === "0") {
        // Automatically select the second checkbox when the mortgage amount is 0
        setSelectedMortgageOption(8);
      } else {
        const propertyValueNum = parseFloat(propertyValue);
        const mortgageAmountNum = parseFloat(mortgageAmount);

        if (propertyValueNum && mortgageAmountNum) {
          const percentage = (mortgageAmountNum / propertyValueNum) * 100;

          if (percentage <= 19) {
            toast.warning(
              "La maggior parte delle banche non forniscono mutui inferiori al 20% del prezzo totale della casa, assicurati che sia possibile."
            );
          } else if (percentage >= 81) {
            toast.warning(
              "La maggior parte delle banche non forniscono mutui superiori all’80% del prezzo totale della casa, assicurati che sia possibile."
            );
          }
        }
      }
    }
  }, [mortgageAmount, propertyValue, isMortgageExpanded]);

  const handleSubmit = async () => {
    setIsLoading(true);

    if (isSubmitted) {
      try {
        if (emails.length > 0) {
          await sendHouseReportPDF(generatedPdfBlob, emails, true);
          toast.success("Report condiviso correttamente.");
          setEmails([]);
          setEmailInput("");
          setGeneratedPdfBlob(null);
          toggleHousePriceCalculatorDialog(id);
        } else {
          setGeneratedPdfBlob(null);
          toggleHousePriceCalculatorDialog(id);
        }
      } catch (error) {
        console.log("error", error);
        toast.error("Error sending the report. Please try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      const firstHomeValue = selectedFirstHomeOption === 1 ? "Y" : "N";
      const sellerValue = selectedSellerOption === 3 ? "N" : "N";
      const agencyValue = selectedAgencyOption === 5 ? "Y" : "N";
      const mortgageValue = selectedMortgageOption === 7 ? mortgageAmount : 0;
      const renditaValue = selectedRenditaOption === 9 ? renditaCatastale : 0;

      // Call the helper function with the appropriate values
      const result = housePriceCalculatorAlogrithm(
        parseFloat(propertyValue),
        firstHomeValue,
        sellerValue,
        agencyValue,
        renditaValue,
        parseFloat(mortgageValue)
      );

      const reportData = {
        propertyValue,
        isRealStateAgency: agencyValue === "Y",
        ...result,
      };

      try {
        // Generate and store the PDF
        const pdfBlob = await sendHouseReportPDF(reportData, [user.email]);
        setGeneratedPdfBlob(pdfBlob); // Store the blob for later use
        toast.success("Report inviato alla tua mail!");
        setIsSubmitted(true);
      } catch (error) {
        toast.error("Error sending the report. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAddEmail = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const email = emailInput.trim();

      if (
        email &&
        !emails.includes(email) &&
        emails.length < MAX_EMAILS &&
        validateEmail(email)
      ) {
        setEmails((prevEmails) => [...prevEmails, email]);
        setEmailInput("");
      }
    }
  };

  // Validate email format
  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  // Handle removal of email
  const handleRemoveEmail = (email) => {
    setEmails(emails.filter((e) => e !== email));
  };

  return (
    <AnimatedDialogLayout
      isOpen={isHousePriceCalculatorDialogOpen === id}
      onClose={() => toggleHousePriceCalculatorDialog(id)}
      styleDropdown={{
        minWidth: isMobile ? "90%" : 514,
        padding: 21,
        paddingRight: 25,
      }}
    >
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-[10px] sm:space-x-[15px] ">
            <div className="bg-[#E8F6FF] min-w-[52px] h-[52px] rounded-full flex items-center justify-center">
              <CalculatorIcon />
            </div>
            <h1 className="text-black max-w-[341px] text-[16px] md:text-[22px] font-semibold leading-[19.36px] md:leading-[30px] line-clamp-4">
              {isSubmitted ? (
                "Report inviato alla tua mail. Vuoi condividerlo con altre persone?"
              ) : (
                <>
                  Scopri il<span className="text-primary"> reale </span>prezzo
                  di una casa calcolando i
                  <span className="text-primary"> costi nascosti</span>
                </>
              )}
            </h1>
          </div>
          <CloseIcon
            onClick={() => toggleHousePriceCalculatorDialog(id)}
            className="cursor-pointer w-[27px] fill-primary h-[27px]"
          />
        </div>
        <div className="sm:px-[33px] mt-[38px] space-y-[10px]">
          {!isSubmitted && (
            <>
              <div className="bg-[#F2F2F2] border-[0.5px] border-[#E5E5E5] rounded-[6px] h-[48px] flex items-center justify-between pl-4 pr-2">
                <p
                  className={`text-primary text-[16px] font-normal ${
                    propertyValue ? "font-semibold" : ""
                  }`}
                >
                  Valore immobile
                </p>
                <div className="relative flex items-center w-[131px]">
                  <input
                    type="text"
                    value={
                      propertyValue ? formatCurrencyInEuro(propertyValue) : ""
                    }
                    onChange={handlePropertyChange}
                    className="bg-[#FFFFFF] h-[34px] outline-none w-full rounded-[5px] px-[13px] text-primary font-semibold text-[14px] pr-[24px] text-right"
                  />
                  <span className="absolute right-[10px] text-primary text-[16px] font-normal">
                    €
                  </span>
                </div>
              </div>

              {/* First Expandable Section: Acquisti come prima casa */}
              <ExpandableSection
                title="Acquisti come prima casa?"
                infoText="La tassazione e i costi differiscono se si acquista come prima casa o come seconda"
                options={firstHomeOptions}
                selectedOption={selectedFirstHomeOption}
                onOptionChange={setSelectedFirstHomeOption}
              />

              {/* Second Expandable Section: Vende un Privato o Impresa */}
              <ExpandableSection
                title="Vende un Privato o Impresa di Costruzioni?"
                infoText="Se vende un’impresa di costruzioni sul prezzo bisognerà pagarci l’iva, rispettivamente al 4% nel caso acquisti come prima casa, al 10% come seconda casa"
                options={sellerOptions}
                selectedOption={selectedSellerOption}
                onOptionChange={setSelectedSellerOption}
              />

              {/* Agency question */}
              <ExpandableSection
                title="Acquisti tramite un'agenzia immobiliare?"
                infoText="Se acquisti tramite un’agenzia immobiliare si prenderà una commissione sul valore della proprietà, secondo la media nazionale è intorno al 3,4%"
                options={agencyOptions}
                selectedOption={selectedAgencyOption}
                onOptionChange={setSelectedAgencyOption}
              />

              {/* Mortgage question with input field */}
              <ExpandableSection
                title="Acquisti con mutuo?"
                infoText="Nel calcolo verrà considerato il costo per l’attivazione del mutuo"
                options={mortgageOptions}
                selectedOption={selectedMortgageOption}
                onOptionChange={setSelectedMortgageOption}
                inputFields={
                  selectedMortgageOption === 7
                    ? [
                        {
                          label: "Importo mutuo",
                          value: mortgageAmount,
                          onChange: (e) =>
                            setMortgageAmount(
                              e.target.value.replace(/\D/g, "")
                            ),
                        },
                      ]
                    : []
                }
                onExpandToggle={toggleMortgageExpand}
              />

              {/* Rendita Catastale question with input field */}
              <ExpandableSection
                title="Conosci la rendita catastale?"
                infoText="La rendita catastale di una proprietà è necessaria al calcolo delle imposte, se non la conosci puoi trovarla sul portale sister dell’agenzia dell’entrate. L’url è sister.agenziaentrate.gov.it/CitizenVisure/"
                options={renditaOptions}
                selectedOption={selectedRenditaOption}
                onOptionChange={setSelectedRenditaOption}
                inputFields={
                  selectedRenditaOption === 9
                    ? [
                        {
                          label: "Rendita Catastale",
                          value: renditaCatastale,
                          onChange: (e) =>
                            setRenditaCatastale(
                              e.target.value.replace(/\D/g, "")
                            ),
                        },
                      ]
                    : []
                }
              />
            </>
          )}

          {isSubmitted && (
            <div>
              <input
                type="email"
                placeholder="Inserisci le email a cui vuoi inviare il report"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={handleAddEmail}
                className="bg-[#F2F2F2] border-[0.5px] border-[#E5E5E5] rounded-[6px] h-[48px] flex items-center justify-between pl-4 pr-2 w-full outline-none text-primary text-[14px] font-medium placeholder:text-gray-400"
              />

              <div className="flex flex-wrap mt-2 max-w-[402px]">
                {emails.map((email, index) => (
                  <div
                    key={index}
                    className="flex items-center bg-[#E5E5E5] rounded-full px-4 py-2 m-1"
                  >
                    <span className="text-primary text-[14px] font-medium">
                      {email}
                    </span>
                    <button
                      onClick={() => handleRemoveEmail(email)}
                      className="ml-2 text-red-500"
                    >
                      <CloseIcon width={9} fill="red" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            disabled={!isFormValid() || isLoading}
            onClick={handleSubmit}
            className="w-full disabled:opacity-45 bg-primary text-white border border-primary rounded-[15px] text-[20px] font-bold text-center cursor-pointer h-[60px] flex items-center justify-center !mt-[34px]"
          >
            {isLoading ? (
              <ButtonSpinner />
            ) : isSubmitted ? (
              emails.length > 0 ? (
                "Invia Calcolo"
              ) : (
                "No, va bene così"
              )
            ) : (
              "Ricevi Calcolo"
            )}
          </button>
        </div>
      </div>
    </AnimatedDialogLayout>
  );
};

export default HousePriceCalculatorDialog;
